var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { forwardRef, useCallback, useRef } from 'react';
import { Button } from '../../atoms/Button/Button';
import './InlineNotificationStyles.scss';
// ─── Dismiss animation ───────────────────────────────────────────────────────
// Mirrors the Toast exit exactly: fade content (60ms) → collapse to a 3px bar
// (140ms) → whoosh off to the right (200ms), then run `onDone`.
function runDismissAnimation(el, onDone) {
    if (el.dataset.dismissing)
        return;
    // No Web Animations API (SSR, old browsers, test env) → remove immediately.
    if (typeof el.animate !== 'function') {
        onDone();
        return;
    }
    el.dataset.dismissing = '1';
    // 1. Fade all child content
    Array.from(el.children).forEach((child) => {
        const c = child;
        c.style.transition = 'opacity 60ms ease';
        c.style.opacity = '0';
    });
    // Lock dimensions and strip the chrome so it collapses cleanly
    const originalHeight = el.offsetHeight;
    el.style.overflow = 'hidden';
    el.style.border = 'none';
    el.style.boxShadow = 'none';
    el.style.transformOrigin = 'center center';
    // 2. Collapse to a 3px bar
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            el.style.transition =
                'transform 140ms cubic-bezier(0.4, 0, 0.2, 1), background 140ms ease, border-radius 140ms ease';
            el.style.transform = `scaleY(${3 / originalHeight})`;
            el.style.background = 'rgba(79, 64, 120, 0.32)';
            el.style.borderRadius = '4px';
        });
    });
    // 3. Whoosh off to the right, then remove
    setTimeout(() => {
        el.style.transition = 'none';
        const scaleY = 3 / originalHeight;
        try {
            el.animate([
                { transform: `scaleY(${scaleY}) translateX(0) scaleX(1) rotate(0deg)`, opacity: 1 },
                { transform: `scaleY(${scaleY}) translateX(18px) scaleX(2.8) rotate(-0.6deg)`, opacity: 0 }
            ], { duration: 200, easing: 'cubic-bezier(0.4, 0, 1, 1)', fill: 'forwards' }).onfinish = onDone;
        }
        catch (_a) {
            onDone();
        }
    }, 155);
}
// ─── Constants ───────────────────────────────────────────────────────────────
const DEFAULT_ICONS = {
    info: React.createElement("i", { className: "fa-light fa-circle-info" }),
    success: React.createElement("i", { className: "fa-light fa-badge-check" }),
    warning: React.createElement("i", { className: "fa-light fa-triangle-exclamation" }),
    error: React.createElement("i", { className: "fa-light fa-diamond-exclamation" }),
    neutral: React.createElement("i", { className: "fa-light fa-circle-info" })
};
// ─── Component ─────────────────────────────────────────────────────────────
/**
 * InlineNotification — a persistent, contextual message that sits inline in page
 * content (e.g. settings pages). The static sibling of the transient `Toast`.
 *
 * @example
 * <InlineNotification variant="info" title="Heads up">
 *   Changes here apply to every building in this account.
 * </InlineNotification>
 *
 * @example
 * // Dismissible, with an action
 * <InlineNotification
 *   variant="warning"
 *   dismissible
 *   onDismiss={handleDismiss}
 *   action={<Button variant="tertiary">Review settings</Button>}
 * >
 *   Two component types are missing a default quantity.
 * </InlineNotification>
 */
export const InlineNotification = forwardRef((_a, ref) => {
    var { variant = 'info', title, children, icon, dismissible = false, onDismiss, action, role, className = '' } = _a, rest = __rest(_a, ["variant", "title", "children", "icon", "dismissible", "onDismiss", "action", "role", "className"]);
    const resolvedRole = role !== null && role !== void 0 ? role : (variant === 'error' ? 'alert' : 'status');
    const ariaLive = resolvedRole === 'alert' ? 'assertive' : 'polite';
    // `undefined` → variant default; `null` → hidden; node → override.
    const resolvedIcon = icon === undefined ? DEFAULT_ICONS[variant] : icon;
    const classes = ['qmb-ui-inline-notification', `qmb-ui-inline-notification--${variant}`, className]
        .filter(Boolean)
        .join(' ');
    // Keep our own handle to the root while still honoring a forwarded ref.
    const innerRef = useRef(null);
    const setRefs = useCallback((node) => {
        innerRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref]);
    const handleDismiss = useCallback(() => {
        var _a;
        const el = innerRef.current;
        const prefersReduced = typeof window !== 'undefined' && ((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(prefers-reduced-motion: reduce)').matches);
        // No element or reduced motion → skip the animation, remove immediately.
        if (!el || prefersReduced) {
            onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss();
            return;
        }
        runDismissAnimation(el, () => onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss());
    }, [onDismiss]);
    return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    React.createElement("div", Object.assign({ ref: setRefs, className: classes, role: resolvedRole, "aria-live": ariaLive }, rest),
        resolvedIcon !== null && (React.createElement("span", { className: "qmb-ui-inline-notification__icon", "aria-hidden": "true" }, resolvedIcon)),
        React.createElement("div", { className: "qmb-ui-inline-notification__content" },
            title && React.createElement("p", { className: "qmb-ui-inline-notification__title" }, title),
            React.createElement("div", { className: "qmb-ui-inline-notification__body" }, children),
            action && React.createElement("div", { className: "qmb-ui-inline-notification__actions" }, action)),
        dismissible && (React.createElement(Button, { className: "qmb-ui-inline-notification__close", "aria-label": "Dismiss", onClick: handleDismiss },
            React.createElement("i", { className: "fa-light fa-xmark" })))));
});
InlineNotification.displayName = 'InlineNotification';
